import React, { useEffect } from "react";
import { useContext, useState } from "react";

import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";

import {
	BusinessDataContext,
	BusinessDataContextValues,
	MultistepContext,
	MultistepContextValues,
} from "../../../types/context";
import { CustomException } from "../../../types/customException";

import dayjs, { Dayjs } from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import "dayjs/locale/es"; // Importa el idioma español de dayjs
import "dayjs/locale/en";
import "dayjs/locale/pt";
import { LangContext } from "../../../types/internationalization";
import { useWindowResize } from "../../../hooks/useWindowResize";
import { styles } from "./FindTable";
import { DatePicker } from "@mui/x-date-pickers";
import { Service } from "../../../types/entities";
import { disableDate } from "../../../utils/shouldDisableDate";
import { findClosestAvailableDate } from "../../../utils/timeUtils";
import { DatesApi } from "../../../api/datesApi";
import { useIntl } from "react-intl";

dayjs.extend(localizedFormat);
dayjs.extend(timezone);
dayjs.extend(utc);

//This is the date select component, it receives the shoulDisableDate according if there is
//or not a service selected, so it knows when to disable the date
export const DateSelect: React.FC<{
	handleError: (error: string, disable: boolean) => void;
	disabled: boolean;
}> = ({ handleError, disabled }) => {
	const { reservation, onChangeDate } = useContext(
		MultistepContext
	) as MultistepContextValues;

	const { offlineDays, serviceAvailabilityException } = useContext(
		BusinessDataContext
	) as BusinessDataContextValues;

	const { formatMessage } = useIntl();

	const langContext = useContext(LangContext);
	const locale = langContext!.locale;
	const timezone = "America/Argentina/Buenos_Aires";
	dayjs.tz.setDefault(timezone);

	const { isMobile, isXSmall } = useWindowResize();

	const service = reservation.service!;

	const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);
	const [offlineDaysByServices, setOfflineDaysByServices] = useState<
		{ date: Date; service: Service }[]
	>([]);

	// Getting offline days by service, the dates that have all the service's shifts disabled
	const getOfflineDaysByService = async (service: Service) => {
		try {
			const datesApi = new DatesApi();
			const result = await datesApi.fetchDaysOfflineByService(service.id!);
			return result.data.map((offlineDay) => ({
				date: new Date(offlineDay.date) ?? offlineDay.date,
				service: service,
			}));
		} catch (error) {
			console.log(error);
			return [];
		}
	};

	// I set to the reservation date, the closest available day, so the DatePicker has a default value
	useEffect(() => {
		const setReservationDate = async () => {
			// If the reservation has time, the selected date will be the same as the time
			if (reservation.time) {
				setSelectedDate(dayjs(reservation.time).utc());
			} else {
				try {
					let offlineServices: { date: Date; service: Service }[] = [];

					// Getting the days whose shifts are disabled in all the services shifts
					const result = await getOfflineDaysByService(service);
					offlineServices = offlineServices.concat(result);

					setOfflineDaysByServices(offlineServices);

					const date = findClosestAvailableDate(
						service,
						offlineDays,
						offlineDaysByServices,
						serviceAvailabilityException
					);
					onChangeDate(date);
					setSelectedDate(date);
				} catch (e) {
					if (e instanceof CustomException) {
						handleError(
							formatMessage({
								id: e.friendlyKey,
								defaultMessage: e.message,
							}),
							true
						);
					}
					handleError(e.message, false);
				}
			}
		};

		setReservationDate();
		// eslint-disable-next-line
	}, [reservation.service]);

	// When the date changes, I update the reservation date
	const handleInputChange = (newValue: Dayjs | null) => {
		if (newValue != null) {
			let time = newValue;
			onChangeDate(time);
			setSelectedDate(time);
		}
	};

	return (
		<div style={{ position: "relative", flex: 1 }}>
			<LocalizationProvider
				dateAdapter={AdapterDayjs}
				adapterLocale={langContext?.locale}
			>
				<DatePicker
					className="datePicker"
					disablePast
					disabled={
						disabled ??
						(reservation.service
							? reservation.service.category.name === "Event"
							: false)
					}
					orientation="portrait"
					value={selectedDate}
					format={
						locale === "en-US"
							? isXSmall
								? "MMM DD"
								: "MMMM DD"
							: isXSmall
							? "DD MMM"
							: "DD MMMM"
					}
					sx={
						isMobile || isXSmall ? styles.datePickerMobile : styles.datePicker
					}
					onChange={handleInputChange}
					shouldDisableDate={disableDate(
						[service],
						offlineDays,
						offlineDaysByServices,
						serviceAvailabilityException
					)}
					slotProps={{
						openPickerButton: {
							size: "small",
						},
						openPickerIcon: {
							fontSize: "small",
						},
					}}
				/>
			</LocalizationProvider>
		</div>
	);
};
